import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_URL = "https://readdy.ai/api/form/db6bp4kbe24agvuu3htg";

type SubmitStatus = "idle" | "loading" | "success" | "error";

export default function About() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [message, setMessage] = useState("");

  usePageMeta({
    title: "关于我们 - 97视频",
    description:
      "了解97视频的网站定位与内容范围，获取联系方式与反馈渠道，查看版权与免责声明信息。",
    keywords: "97视频,关于我们,联系我们,免责声明,反馈",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      setMessage("");
      return;
    }
    formData.delete("company_alt");

    setStatus("loading");
    setErrorMsg("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        formEl.reset();
        setMessage("");
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setErrorMsg("网络异常，请稍后重试");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-gradient-to-r from-primary-900/60 to-background-100">
          <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
            <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              关于97视频
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              97视频是一个综合影视在线观看平台，致力于汇聚电影、电视剧、综艺节目等丰富影视内容，
              为广大影视爱好者提供清晰、便捷、舒适的浏览与观看体验。
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 py-12 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div className="space-y-8">
              <div>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-compass-3-line text-accent-500" aria-hidden="true"></i>
                  网站定位
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  97视频面向追求高清、多样影视内容的各年龄段观众，提供快速查找、分类筛选、
                  内容简介与相关推荐等能力，让每一次选片都更加轻松高效。
                </p>
              </div>

              <div>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-stack-line text-accent-500" aria-hidden="true"></i>
                  内容范围
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  平台内容覆盖电影、电视剧、综艺三大板块，涵盖剧情、科幻、悬疑、动作、
                  古装、真人秀等多种类型，并持续更新最新最热的影视资源与主题专题合集。
                </p>
                <div className="mt-4 flex flex-wrap gap-3 text-sm">
                  <Link
                    to="/category/movie"
                    className="cursor-pointer whitespace-nowrap rounded-md bg-background-100 px-4 py-2 text-foreground-700 hover:bg-background-200"
                  >
                    <i className="ri-film-line mr-1.5 text-primary-500" aria-hidden="true"></i>
                    电影
                  </Link>
                  <Link
                    to="/category/tv"
                    className="cursor-pointer whitespace-nowrap rounded-md bg-background-100 px-4 py-2 text-foreground-700 hover:bg-background-200"
                  >
                    <i className="ri-tv-2-line mr-1.5 text-accent-500" aria-hidden="true"></i>
                    电视剧
                  </Link>
                  <Link
                    to="/category/variety"
                    className="cursor-pointer whitespace-nowrap rounded-md bg-background-100 px-4 py-2 text-foreground-700 hover:bg-background-200"
                  >
                    <i className="ri-mic-line mr-1.5 text-secondary-500" aria-hidden="true"></i>
                    综艺
                  </Link>
                </div>
              </div>

              <div>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-shield-check-line text-accent-500" aria-hidden="true"></i>
                  版权与免责声明
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  本站当前为前端演示站点，展示的影视信息、封面图片与简介均为模拟数据，仅用于
                  功能与界面展示，不提供任何真实影视资源的播放、下载或传播服务。如涉及版权事宜，
                  请通过下方表单与我们联系。
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-mail-send-line text-accent-500" aria-hidden="true"></i>
                联系我们
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                有任何建议、合作意向或版权问题，欢迎留言与我们反馈
              </p>

              <form
                id="about-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 space-y-4"
              >
                <div className="field-assist-note">
                  <label htmlFor="company_alt">公司名称</label>
                  <input
                    type="text"
                    id="company_alt"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    姓名
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入您的姓名"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入您的邮箱"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="请输入您的建议或反馈内容（限500字以内）"
                    className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none"
                  ></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-500">
                    {message.length}/500
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "loading" ? "提交中..." : "提交反馈"}
                </button>

                {status === "success" && (
                  <p className="flex items-center gap-1.5 text-sm text-primary-600">
                    <i className="ri-checkbox-circle-fill" aria-hidden="true"></i>
                    感谢您的反馈，我们已收到您的留言！
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-1.5 text-sm text-red-500">
                    <i className="ri-error-warning-fill" aria-hidden="true"></i>
                    {errorMsg}
                  </p>
                )}
              </form>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/"
              className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-foreground-600 hover:text-accent-500"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回首页
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}